import useSWRImmutable from 'swr/immutable';
import urlResolver from 'admin/utils/URLResolver';
import { loadCourseBranches } from 'admin/domains/branches/api';

export const useCourseSelector = (
  // defaultSearchByPublicationYear is the default year (i.e. 2004 atm) to filter by when searching for courses.
  // You could list other years by simply typing 2023, 2022, etc. (see searchForWordsAndFilterByYear below).
  defaultSearchByPublicationYear = window.__current_academic_year__
) => {
  // calls api.views.common.BranchViewSet.for_select_courses
  const { data, error: courseOptionsError } = useSWRImmutable(
    urlResolver.reverse('api:branch-for-select-courses'),
    loadCourseBranches
  );

  const courseOptions =
    data
      ?.sortBy((entry) => entry.get('title'))
      .reverse()
      .sortBy((entry) => entry.get('year_published'))
      .reverse()
      .toJS() ?? [];

  const searchForWordsAndFilterByYear = (option, filterValue) => {
    const { year_published: yearPublished } = option.data;
    const testString = option.label.toLowerCase();

    // Can't use _.words because it splits "Y10" into ["Y", "10"]
    const filterWords = filterValue
      .toLowerCase()
      .split(' ')
      .filter((w) => w.length > 0);

    // If a number like 20xx is entered, filter to that publication year,
    // otherwise filter to the defaultSearchByPublicationYear or any subsequent years.
    const yearBeingSearchedFor =
      parseInt(filterWords.find((word) => word.match(/20\d\d/))) || null;
    const entryMatchesYear = yearBeingSearchedFor
      ? yearPublished === yearBeingSearchedFor
      : yearPublished >= defaultSearchByPublicationYear;

    // If we're not searching for a year but three or more digits are entered,
    // it might be a course id, so look at all years
    const numberBeingSearchedFor = filterWords.find((word) =>
      word.match(/(id:)?\d{3,}/)
    );
    if (!yearBeingSearchedFor && numberBeingSearchedFor) {
      // Limit the search to numbers starting with the search term,
      // so searching for "123" matches "1234" but not "2123"
      const foundNumberStartingWithSearch = testString.match(
        new RegExp(`\\D${numberBeingSearchedFor}`, 'g')
      );
      return (
        foundNumberStartingWithSearch &&
        filterWords.every((word) => testString.includes(word))
      );
    }

    return (
      entryMatchesYear && filterWords.every((word) => testString.includes(word))
    );
  };

  return {
    courseOptions,
    courseOptionsError,
    isLoading: !data && !courseOptionsError,
    searchForWordsAndFilterByYear,
  };
};
